// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying item triage entries.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemTriage, ItemTriageFilter } from '@/items/api/triage-types';
import { TriageClassifySkuModal } from '@/items/components/triage-classify-sku-modal';
import { TriageDismissDialog } from '@/items/components/triage-dismiss-dialog';
import { TriageTableRow } from '@/items/components/triage-table-row';
import { useTriageItems } from '@/items/hooks/use-triage-items';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 50;

interface TriageTableProps {
  filter?: ItemTriageFilter;
  onPageChange: (page: number) => void;
}

/**
 * Table component for displaying item triage entries with actions.
 */
export function TriageTable(props: TriageTableProps) {
  const { items, resultCount } = useTriageItems({ filter: props.filter });
  const classifyModal = useDisclosure();
  const dismissDialog = useDisclosure();
  const [selectedItem, setSelectedItem] = useState<ItemTriage | null>(null);
  const [itemToDismiss, setItemToDismiss] = useState<ItemTriage | null>(null);

  const pageNumber = props.filter?.page ?? 1;
  const onPageChange = props.onPageChange;

  const handleClassifyClick = (item: ItemTriage) => {
    setSelectedItem(item);
    classifyModal.open();
  };

  const handleDismissClick = (item: ItemTriage) => {
    setItemToDismiss(item);
    dismissDialog.open();
  };

  const handleModalClose = () => {
    classifyModal.close();
    setSelectedItem(null);
  };

  const handleDismissDialogClose = () => {
    dismissDialog.close();
    setItemToDismiss(null);
  };

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && items.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [items.length, pageNumber, onPageChange]);

  if (items.length === 0) {
    const getEmptyStateContent = () => {
      switch (props.filter?.status) {
        case 'MATCHED':
          return {
            statusLabel: 'matched',
            message:
              'SKUs that have been classified in Hardfin will appear here',
          };
        case 'DISMISSED':
          return {
            statusLabel: 'dismissed',
            message:
              'SKUs that have been dismissed from the review queue will appear here',
          };
        default:
          return {
            statusLabel: 'need review',
            message:
              'SKUs from external systems that need classification in Hardfin will appear here',
          };
      }
    };
    const emptyState = getEmptyStateContent();
    return (
      <EmptyState
        title={`No items ${emptyState.statusLabel}`}
        message={emptyState.message}
      />
    );
  }

  return (
    <>
      <TriageClassifySkuModal
        isOpen={classifyModal.isOpen}
        item={selectedItem}
        onClose={handleModalClose}
      />
      <TriageDismissDialog
        isOpen={dismissDialog.isOpen}
        item={itemToDismiss}
        onClose={handleDismissDialogClose}
      />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} align="center">
                Source
              </TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>First synced</TableCell>
              <TableCell>Imported on</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Item type</TableCell>
              <TableCell width={200} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TriageTableRow
                key={item.id}
                item={item}
                onClassifyClick={handleClassifyClick}
                onDismissClick={handleDismissClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize: PAGE_SIZE,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{
          singular: 'item',
          plural: 'items',
          includeCount: false,
        }}
      />
    </>
  );
}
